import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import AuthModal from "./components/AuthModal";
import Carousel from "./components/Carousel";
import SearchBar from "./components/SearchBar";
import Header from './components/Header';
import Footer from './components/Footer';
import DetailPage from './components/DetailPage';
import Testimonials from "./components/Testimonials";
import FinancingPage from './components/FinancingPage';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ShoppingCart,
  X,
  ChevronLeft,
  Phone,
  MessageCircle,
  Plus,
  Minus,
  Trash2,
  Download,
  Send,
  ChevronRight,
  Fuel,
  Settings,
  Mail,
  Calendar,
  Car,
  Anchor,
  Home as HomeIcon,
  ArrowRight
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL;

function formatPrice(xpf) {
  if (typeof xpf !== 'number') return xpf;
  return xpf.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' XPF';
}


function VehicleShop() {
  const navigate = useNavigate();
  const location = useLocation();
  const [language, setLanguage] = useState('fr');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [vehicles, setVehicles] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  // Ref pour éviter de recharger le panier plusieurs fois (closure stable)
  const cartLoadedRef = React.useRef(false);
  const handleLogout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setIsAuthenticated(false);
    setUser(null);
    setCart([]);
    cartLoadedRef.current = false;
    toast.info('Déconnexion réussie', { theme: 'colored' });
  };

  // ✅ Scroll automatique en haut à chaque changement de page
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [location.pathname]);

  // ✅ Fonction de recherche optimisée
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  const translations = {
    fr: {
      home: "Accueil",
      ourStock: "Notre Stock",
      cart: "Panier",
      login: "Connexion",
      logout: "Déconnexion",
      financing: "Financement",
      exceptionalOffers: "Offres exceptionnelles: import direct Pologne → Outre-mer",
      financingTitle: "Solutions de Financement",
      monthlyPayment: "Paiement mensuel",
      startingFrom: "À partir de",
      financingDesc: "Découvrez nos solutions de financement adaptées",
      categories: "Catégories",
      brands: "Marques",
      models: "Modèles",
      addToCart: "Ajouter au panier",
      contactSeller: "Contacter le vendeur",
      backToBrowsing: "Continuer la visite",
      year: "Année",
      price: "Prix",
      power: "Puissance",
      back: "Retour",
      fuel: "Carburant",
      mileage: "Kilométrage",
      transmission: "Transmission",
      seats: "Places",
      color: "Couleur",
      viewAllStock: "Voir tout le stock",
      emptyCart: "Votre panier est vide",
      total: "Total",
      exportWhatsApp: "Envoyer via WhatsApp",
      downloadOrder: "Télécharger la commande",
      loginTitle: "Connexion",
      registerTitle: "Inscription",
      email: "Email",
      password: "Mot de passe",
      loginButton: "Se connecter",
      registerButton: "S'inscrire",
      noAccount: "Pas de compte ?",
      hasAccount: "Déjà un compte ?",
      characteristics: "Caractéristiques",
      discover: "DÉCOUVRIR",
      discoverCollection: "Découvrez Notre Collection",
      clickCategory: "Cliquez sur une catégorie pour explorer notre stock",
      callUs: "Nous Contacter",
      search: "Rechercher une voiture, marque..."
    },
    en: {
      home: "Home",
      ourStock: "Our Stock",
      cart: "Cart",
      login: "Login",
      logout: "Logout",
      exceptionalOffers: "Exceptional offers: direct import Poland → Overseas",
      search: "Search car, brand..."
    }
  };

  const t = translations[language];

  // ✅ CORRECTION : Vérifier le format des données reçues
  useEffect(() => {
  fetch(`${API_BASE}/api/vehicles`)
    .then(r => r.json())
    .then(data => {
      if (Array.isArray(data)) {
        setVehicles(data);
      } else {
        console.error('L\'API n\'a pas renvoyé un tableau:', data);
        setVehicles([]);
      }
    })
    .catch(err => {
      console.error('Erreur réseau / Backend éteint:', err);
      setVehicles([]);
    });
}, [location.pathname]);
  // Récupérer le token du localStorage au démarrage
  useEffect(() => {
    const savedToken = localStorage.getItem("token");
    if (savedToken) {
      fetch(`${API_BASE}/api/auth/verify`, {
        headers: { Authorization: `Bearer ${savedToken}` },
      })
        .then(res => {
          if (!res.ok) throw new Error("Token invalide");
          return res.json();
        })
        .then(data => {
          setToken(savedToken);
          setIsAuthenticated(true);
          if (data.user) setUser(data.user);
        })
        .catch(() => {
          localStorage.removeItem("token");
          setToken(null);
          setIsAuthenticated(false);
        });
    }
  }, []);


  // Charger le panier une seule fois quand on a token + véhicules chargés
  useEffect(() => {
    if (!token) {
      cartLoadedRef.current = false;
      return;
    }
    if (vehicles.length === 0 || cartLoadedRef.current) return;
    cartLoadedRef.current = true;

    fetch(`${API_BASE}/api/cart`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => {
        if (!r.ok) throw new Error('Erreur chargement panier');
        return r.json();
      })
      .then(data => {
        console.log('📦 Panier reçu du serveur:', data);
        const enriched = enrichCartItems(data, vehicles);
        if (enriched !== null) setCart(enriched);
      })
      .catch(err => {
        console.error('❌ Erreur chargement panier:', err);
        setCart([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, vehicles.length]);



  // ── Comparaison _id robuste : string "1" === ObjectId("1") ─────────
  // ── Helper : extrait l'ID d'un véhicule sous forme String ─────────
  // MongoDB stocke _id comme Number (1,2,3) après migration
  const getVehicleId = (vehicle) => {
    if (!vehicle) return undefined;
    const raw = vehicle._id !== undefined ? vehicle._id : vehicle.vehicleId;
    if (raw === undefined || raw === null || String(raw) === 'undefined') return undefined;
    return String(raw);
  };

  // ── Comparaison d'ID robuste ─────────────────────────────────────────
  const matchId = (a, b) => {
    if (a === undefined || a === null || b === undefined || b === null) return false;
    return String(a) === String(b);
  };

  // ── Enrichir les items serveur avec les données véhicule locales ────
  const enrichCartItems = (cartItems, vehiclesList = vehicles) => {
    if (!Array.isArray(cartItems)) {
      console.error('❌ enrichCartItems: réponse non-array:', cartItems);
      return null;
    }
    return cartItems
      .map(item => {
        if (!item.vehicleId || String(item.vehicleId) === 'undefined') {
          console.warn('⚠️ item.vehicleId invalide, ignoré:', item);
          return null;
        }
        const v = vehiclesList.find(vv => String(vv._id) === String(item.vehicleId));
        if (!v) {
          console.warn('⚠️ Véhicule introuvable pour vehicleId:', item.vehicleId);
          return null;
        }
        return { ...v, cartItemId: item._id, vehicleId: String(item.vehicleId), quantity: item.qty };
      })
      .filter(Boolean);
  };

  const addToCart = async (vehicle) => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    // ✅ Guard : vérifier que l'ID est valide AVANT d'envoyer au serveur
    const vehicleId = getVehicleId(vehicle);
    if (!vehicleId || vehicleId === 'undefined') {
      console.error('❌ addToCart: _id invalide sur le véhicule', vehicle);
      toast.error('Erreur : véhicule sans identifiant valide', { theme: 'colored' });
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/cart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ vehicleId, qty: 1 }),
      });

      const data = await res.json();
      console.log('🛒 Réponse serveur addToCart:', data);

      if (!res.ok) throw new Error(data.message || `Erreur serveur ${res.status}`);

      const enriched = enrichCartItems(data, vehicles);
      if (enriched !== null) setCart(enriched);

      toast.success(`${vehicle.name} ajouté au panier !`, {
        position: 'top-right',
        autoClose: 2500,
        theme: 'colored',
      });
    } catch (err) {
      console.error('❌ addToCart:', err.message);
      toast.error(`Impossible d'ajouter : ${err.message}`, { theme: 'colored' });
    }
  };

  const updateQuantity = (id, delta) => {
    setCart((prev) => {
      const target = prev.find((i) => matchId(i.vehicleId, id));
      if (!target) return prev;
      const newQty = (target.quantity || 0) + delta;

      if (token) {
        if (newQty <= 0) {
          removeFromCart(id);
        } else {
          fetch(`${API_BASE}/api/cart`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ vehicleId: getVehicleId(target), qty: delta }),
          })
            .then(r => r.json())
            .then(data => {
              const enriched = enrichCartItems(data, vehicles);
              if (enriched !== null) setCart(enriched);
            })
            .catch(() => { });
        }
      }

      // Mise à jour optimiste locale
      return prev
        .map(item => (matchId(item.vehicleId, id) ? { ...item, quantity: newQty } : item))
        .filter(item => (matchId(item.vehicleId, id) ? newQty > 0 : true));
    });
  };

  const removeFromCart = async (itemId) => {
    try {
      const itemToRemove = cart.find(item => matchId(item.vehicleId, itemId));
      if (!itemToRemove) return;

      const vehicleIdToRemove = getVehicleId(itemToRemove);

      // Optimiste : retirer immédiatement de l'UI
      setCart(prev => prev.filter(item => !matchId(item.vehicleId, vehicleIdToRemove)));

      const res = await fetch(`${API_BASE}/api/cart/${vehicleIdToRemove}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      const enriched = enrichCartItems(data, vehicles);
      if (enriched !== null) setCart(enriched);

      toast.info(`${itemToRemove?.name || 'Article'} retiré du panier`, { theme: 'colored' });
    } catch (err) {
      console.error('❌ removeFromCart:', err.message);
      toast.error('Erreur lors de la suppression', { theme: 'colored' });
    }
  };


  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const exportToWhatsApp = () => {
    const message = `🛒 *Ma Commande Elite Motors*\n\n${cart.map(item =>
      `${item.name}\nQuantité: ${item.quantity}\nPrix: ${formatPrice(item.price)}`
    ).join('\n\n')}\n\n*Total: ${formatPrice(cartTotal)}*`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`);
  };

  const downloadOrder = () => {
    const content = `COMMANDE ELITE MOTORS\n\n${cart.map(item =>
      `${item.name}\nQuantité: ${item.quantity}\nPrix unitaire: ${formatPrice(item.price)}\nSous-total: ${formatPrice(item.price * item.quantity)}`
    ).join('\n\n')}\n\nTOTAL: ${formatPrice(cartTotal)}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'commande-elite-motors.txt';
    a.click();
  };


  // 🚀 HOMEPAGE — Style Elite Automotive

  const categoryInfo = {
    Automobiles: { name: 'Automobiles', icon: '🚗', route: '/automobiles', categories: ['Voiture', 'Moto'] },
    Bateaux: { name: 'Bateaux', icon: '🚤', route: '/bateaux', categories: ['Bateau'] },
    Bungalows: { name: 'Bungalows', icon: '🏡', route: '/bungalows', categories: ['Bungalow'] },
  };
  const categoryIcon = { Voiture: '🚗', Moto: '🏍️', Bateau: '🚤' };

  // ── Images HD par catégorie ──────────────────────────────────────────
  const categoryImages = {
    Automobiles: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1200&q=80',
    Bateaux: 'https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=1200&q=80',
    Bungalows: 'https://images.unsplash.com/photo-1510798831971-661eb04b3739?w=1200&q=80',
  };

  // ── Carte véhicule premium (réutilisable partout) ────────────────────
  const PremiumVehicleCard = ({ vehicle, accentColor = 'red' }) => {
    const accentBorder = accentColor === 'orange' ? 'group-hover:border-orange-400' : accentColor === 'blue' ? 'group-hover:border-blue-400' : 'group-hover:border-red-600';
    const accentText = accentColor === 'orange' ? 'text-orange-500' : accentColor === 'blue' ? 'text-blue-500' : 'text-red-600';
    const accentBadgeBg = accentColor === 'orange' ? 'bg-orange-50 text-orange-600' : accentColor === 'blue' ? 'bg-blue-50 text-blue-600' : 'bg-red-50 text-red-600';

    return (
      <div
        onClick={() => { setSelectedVehicle(vehicle); navigate('/detail'); }}
        className={`group bg-white rounded-2xl overflow-hidden border-2 border-gray-100 ${accentBorder} hover:shadow-2xl transition-all duration-500 cursor-pointer relative flex flex-col`}
      >
        {/* Image */}
        <div className="h-52 overflow-hidden relative bg-gray-100 flex-shrink-0">
          {vehicle.images?.[0] ? (
            <img
              src={vehicle.images[0]}
              alt={vehicle.name}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              loading="lazy"
              onError={e => { e.target.src = 'https://via.placeholder.com/400x300/f3f4f6/9ca3af?text=Image+Indisponible'; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-50">
              <span className="text-6xl opacity-20">{categoryIcon[vehicle.category] || '🚗'}</span>
            </div>
          )}
          {/* Overlay hover */}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <span className="bg-white text-gray-900 px-6 py-2 rounded-full font-black uppercase text-[10px] tracking-widest transform translate-y-4 group-hover:translate-y-0 transition-transform">
              Voir la fiche
            </span>
          </div>
          {/* Badge année */}
          <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-2.5 py-1 rounded-lg shadow-sm">
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-900">{vehicle.year}</span>
          </div>
          {/* Badge catégorie */}
          <div className={`absolute top-3 right-3 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${accentBadgeBg}`}>
            {vehicle.category}
          </div>
        </div>

        {/* Infos */}
        <div className="p-5 flex flex-col flex-1">
          <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${accentText} mb-1`}>
            {vehicle.brand}
          </span>
          <h3 className="font-black text-gray-900 text-sm leading-tight mb-4 line-clamp-1 group-hover:text-gray-700 transition-colors flex-1">
            {vehicle.name}
          </h3>

          {/* Badges techniques — Lucide icons, sans emoji */}
          <div className="flex flex-wrap gap-1.5 mb-4">
            {vehicle.fuel && (
              <span className="flex items-center gap-1 text-[10px] bg-gray-50 border border-gray-100 text-gray-600 font-black uppercase tracking-widest px-2 py-1 rounded-lg">
                <Fuel size={10} className="flex-shrink-0" /> {vehicle.fuel.split('/')[0].trim()}
              </span>
            )}
            {vehicle.transmission && (
              <span className="flex items-center gap-1 text-[10px] bg-gray-50 border border-gray-100 text-gray-600 font-black uppercase tracking-widest px-2 py-1 rounded-lg">
                <Settings size={10} className="flex-shrink-0" /> {vehicle.transmission.split('/')[0].trim()}
              </span>
            )}
            {vehicle.power && !vehicle.fuel && (
              <span className="flex items-center gap-1 text-[10px] bg-gray-50 border border-gray-100 text-gray-600 font-black uppercase tracking-widest px-2 py-1 rounded-lg">
                <Settings size={10} className="flex-shrink-0" /> {vehicle.power.split('(')[0].trim()}
              </span>
            )}
          </div>

          {/* Prix + CTA */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <div>
              <p className="text-[9px] text-gray-400 font-black uppercase tracking-[0.2em] mb-0.5">Prix Import</p>
              <p className="font-black text-gray-900 text-base leading-none">{formatPrice(vehicle.price)}</p>
            </div>
            <div className="w-9 h-9 rounded-full bg-gray-900 group-hover:bg-red-600 flex items-center justify-center transition-colors duration-300 shadow-md flex-shrink-0">
              <ChevronRight size={16} className="text-white" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 🚀 HOMEPAGE — Style Elite Automotive
  const HomePage = () => {
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
      if (vehicles.length > 0) setIsLoading(false);
    }, [vehicles.length]);

    const hasValidSearch = searchQuery && searchQuery.trim().length > 0;
    const filteredVehicles = hasValidSearch
      ? vehicles.filter(v => {
          const searchText = `${v.name} ${v.brand} ${v.category}`.toLowerCase();
          return searchText.includes(searchQuery.toLowerCase());
        })
      : [];

    // ── Config sections horizontales ─────────────────────────────────
    const sections = [
      {
        catKey: 'Automobiles',
        index: '01',
        title: 'Automobiles',
        pitch: 'Performance et importation sur mesure. Voitures et motos sélectionnées en Europe.',
        img: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1400&q=85',
        reverse: false,
      },
      {
        catKey: 'Bateaux',
        index: '02',
        title: 'Bateaux',
        pitch: 'Embarcations de qualité pour naviguer en toute liberté sur les eaux de l\'Outre-Mer.',
        img: 'https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=1400&q=85',
        reverse: true,
      },
      {
        catKey: 'Bungalows',
        index: '03',
        title: 'Bungalows',
        pitch: 'Habitat modulaire et solutions clé en main, livrés directement sur votre terrain.',
        img: 'https://images.unsplash.com/photo-1510798831971-661eb04b3739?w=1400&q=85',
        reverse: false,
      },
    ];

    // ── Section bande horizontale ─────────────────────────────────────
    const WideSection = ({ section, idx }) => {
      const cat = categoryInfo[section.catKey];
      const count = vehicles.filter(v => cat.categories.includes(v.category)).length;
      const isComingSoon = count === 0;

      // Prendre jusqu'à 5 vraies photos du stock pour cette catégorie
      const stockPhotos = vehicles
        .filter(v => cat.categories.includes(v.category) && v.images?.[0])
        .slice(0, 5)
        .map(v => v.images[0]);

      // Compléter avec l'image Unsplash si pas assez de photos
      while (stockPhotos.length < 5) stockPhotos.push(section.img);

      // Layouts des 5 images : rotations et positions décalées
      const photoStyles = [
        { rotate: '-rotate-3',  scale: 'scale-100', z: 'z-30', pos: 'top-[8%] left-[5%]',   w: 'w-[48%]', aspect: 'aspect-[4/3]', shadow: 'shadow-2xl' },
        { rotate: 'rotate-2',   scale: 'scale-100', z: 'z-20', pos: 'top-[5%] left-[42%]',  w: 'w-[42%]', aspect: 'aspect-[3/4]', shadow: 'shadow-xl'  },
        { rotate: '-rotate-1',  scale: 'scale-100', z: 'z-40', pos: 'top-[38%] left-[20%]', w: 'w-[50%]', aspect: 'aspect-[4/3]', shadow: 'shadow-2xl' },
        { rotate: 'rotate-3',   scale: 'scale-100', z: 'z-10', pos: 'top-[42%] left-[62%]', w: 'w-[30%]', aspect: 'aspect-square', shadow: 'shadow-lg'  },
        { rotate: '-rotate-2',  scale: 'scale-100', z: 'z-50', pos: 'top-[68%] left-[8%]',  w: 'w-[35%]', aspect: 'aspect-[4/3]', shadow: 'shadow-xl'  },
      ];

      return (
        <div className={`flex flex-col ${section.reverse ? 'md:flex-row-reverse' : 'md:flex-row'} w-full min-h-[60vh] md:min-h-[70vh]`}>

          {/* ── Collage photos ── */}
          <div className="relative w-full md:w-[58%] flex-shrink-0 bg-gray-50 overflow-hidden"
            style={{ minHeight: 'min(75vw, 520px)' }}>

            {/* Fond texturé subtil */}
            <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-50" />

            {/* Photos en collage superposé */}
            {stockPhotos.map((photo, i) => {
              const s = photoStyles[i];
              return (
                <div
                  key={i}
                  className={`absolute ${s.pos} ${s.w} ${s.z} ${s.rotate} ${s.shadow} overflow-hidden rounded-xl border-2 border-white transition-transform duration-500 hover:scale-105 hover:z-[60]`}
                >
                  <div className={`${s.aspect} w-full`}>
                    <img
                      src={photo}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={e => { e.target.src = section.img; }}
                    />
                  </div>
                </div>
              );
            })}

            {/* Dégradé de sortie vers le côté contenu */}
            <div className={`absolute inset-y-0 ${section.reverse ? 'left-0' : 'right-0'} w-16 bg-gradient-to-${section.reverse ? 'r' : 'l'} from-white/60 to-transparent z-[70]`} />
          </div>

          {/* ── Contenu 42% ── */}
          <div
            className="relative w-full md:w-[42%] flex flex-col justify-center px-8 sm:px-10 md:px-14 py-12 md:py-20 bg-white overflow-hidden cursor-pointer group"
            onClick={() => { if (!isComingSoon) navigate(cat.route); }}
          >
            {/* Numéro filigrane */}
            <span className="absolute -bottom-4 right-4 text-[8rem] md:text-[11rem] font-black italic leading-none text-gray-100 select-none pointer-events-none">
              {section.index}
            </span>

            <div className="relative z-10">
              {/* Badge stock */}
              <div className="mb-5">
                <span className={`inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1.5 rounded-full ${isComingSoon ? 'bg-gray-100 text-gray-400' : 'bg-gray-900 text-white'}`}>
                  {isComingSoon ? 'Bientôt disponible' : `${count} modèle${count > 1 ? 's' : ''} en stock`}
                </span>
              </div>

              {/* Titre */}
              <h2 className="text-4xl sm:text-5xl md:text-[3.5rem] font-black italic uppercase tracking-tighter text-gray-900 leading-none mb-4">
                {section.title}
              </h2>

              {/* Description */}
              <p className="text-gray-500 font-medium text-sm leading-relaxed mb-8 max-w-xs">
                {section.pitch}
              </p>

              {/* CTA ligne rouge */}
              {!isComingSoon && (
                <div className="group/cta flex items-center gap-4">
                  <div className="h-px w-10 bg-red-600 group-hover/cta:w-16 transition-all duration-500" />
                  <span className="text-[11px] font-black uppercase tracking-[0.25em] text-gray-900 group-hover/cta:text-red-600 transition-colors duration-300">
                    Explorer la collection
                  </span>
                  <ArrowRight size={14} className="text-red-600 group-hover/cta:translate-x-2 transition-transform duration-300" />
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="bg-white">
        {!hasValidSearch && (
          <>
            {/* ── Intro header ─────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto px-6 sm:px-8 pt-16 pb-12 sm:pt-24 sm:pb-20">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8 border-b border-gray-100 pb-12">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.35em] text-red-600 mb-4">
                    — Collection exclusive
                  </p>
                  <h1 className="text-4xl sm:text-6xl md:text-7xl font-black italic uppercase tracking-tighter text-gray-900 leading-none">
                    Import Direct<br /><span className="text-red-600">Pologne</span>
                  </h1>
                </div>
                {/* Stats */}
                <div className="flex gap-10 sm:gap-12 flex-shrink-0">
                  {[
                    { value: vehicles.length, label: 'Véhicules' },
                    { value: new Set(vehicles.map(v => v.brand)).size, label: 'Marques' },
                  ].map(({ value, label }) => (
                    <div key={label} className="text-right">
                      <p className="text-4xl font-black italic text-red-600 leading-none">{value}</p>
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mt-1">{label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Sections bandes ──────────────────────────────────── */}
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 px-6 sm:px-8 max-w-7xl mx-auto">
                {[1,2,3].map(i => (
                  <div key={i} className="w-full bg-gray-100 animate-pulse rounded-2xl" style={{ height: '420px' }} />
                ))}
              </div>
            ) : (
              <div className="px-6 sm:px-8 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {sections.map((section) => {
                    const cat = categoryInfo[section.catKey];
                    const count = vehicles.filter(v => cat.categories.includes(v.category)).length;
                    const isComingSoon = count === 0;
                    const firstPhoto = vehicles.find(v => cat.categories.includes(v.category) && v.images?.[0])?.images[0] || section.img;
                    return (
                      <div
                        key={section.catKey}
                        className="relative rounded-2xl overflow-hidden cursor-pointer group"
                        style={{ height: '420px' }}
                        onClick={() => { if (!isComingSoon) navigate(cat.route); }}
                      >
                        {/* Image */}
                        <img
                          src={firstPhoto}
                          alt={section.title}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          loading="lazy"
                          onError={e => { e.target.src = section.img; }}
                        />
                        {/* Dégradé sombre bas */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                        {/* Badge count */}
                        <div className="absolute top-4 right-4">
                          <span className="bg-red-600 text-white text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full">
                            {isComingSoon ? 'Bientôt' : `${count} véhicules`}
                          </span>
                        </div>

                        {/* Contenu bas */}
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <div className="h-0.5 w-8 bg-red-600 mb-3" />
                          <h3 className="text-white font-black text-2xl uppercase italic tracking-tight">
                            {section.title}
                          </h3>
                          <p className="text-white/70 text-xs font-bold uppercase tracking-widest mt-1 flex items-center gap-2 group-hover:text-white transition-colors">
                            Découvrir la collection
                            <span className="transition-transform group-hover:translate-x-1">→</span>
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── CTA tout le stock ─────────────────────────────────── */}
            <div className="py-20 sm:py-28 px-6 sm:px-8">
              <div className="max-w-7xl mx-auto">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8 border-t border-gray-100 pt-12">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-400 mb-3">— Catalogue complet</p>
                    <h3 className="text-3xl sm:text-4xl font-black italic uppercase tracking-tighter text-gray-900">
                      {vehicles.length} véhicules<br />disponibles
                    </h3>
                  </div>
                  <button
                    onClick={() => navigate('/stock')}
                    className="group flex items-center gap-4 cursor-pointer"
                  >
                    <div className="h-px w-10 bg-red-600 group-hover:w-16 transition-all duration-500" />
                    <span className="text-[11px] font-black uppercase tracking-[0.25em] text-gray-900 group-hover:text-red-600 transition-colors">
                      Voir tout le stock
                    </span>
                    <ArrowRight size={14} className="text-red-600 group-hover:translate-x-2 transition-transform duration-300" />
                  </button>
                </div>
              </div>
            </div>

            {/* ── Témoignages ──────────────────────────────────────── */}
            <div className="pb-20 px-6 sm:px-8">
              <div className="max-w-7xl mx-auto">
                <Testimonials />
              </div>
            </div>
          </>
        )}

        {/* ── Mode recherche ────────────────────────────────────────── */}
        {hasValidSearch && (
          <div className="max-w-7xl mx-auto px-4 py-12">
            <div className="bg-white rounded-2xl shadow-sm p-8">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-3xl font-black italic uppercase text-gray-900 mb-2">
                    Résultats de <span className="text-red-600">Recherche</span>
                  </h3>
                  <p className="text-gray-500 font-bold text-sm">
                    {filteredVehicles.length} véhicule{filteredVehicles.length > 1 ? 's' : ''} pour "{searchQuery}"
                  </p>
                </div>
                <button
                  onClick={() => setSearchQuery('')}
                  className="px-6 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-black uppercase tracking-widest text-gray-700 transition-all flex items-center gap-2"
                >
                  <X size={14} /> Effacer
                </button>
              </div>
              {filteredVehicles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredVehicles.map(vehicle => (
                    <PremiumVehicleCard key={vehicle._id} vehicle={vehicle} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-5xl font-black italic uppercase text-gray-100 mb-4">Aucun résultat</p>
                  <p className="text-gray-500 font-bold mb-8">
                    Aucun véhicule ne correspond à "{searchQuery}".
                  </p>
                  <button onClick={() => setSearchQuery('')}
                    className="px-8 py-4 bg-red-600 text-white rounded-xl font-black uppercase text-xs tracking-widest hover:bg-red-700 transition-all">
                    Voir les catégories
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ══════════════════════════════════════════════════════════════════════
  // 🚗 AUTOMOBILES PAGE
  // ══════════════════════════════════════════════════════════════════════
  const AutomobilesPage = () => {
    const [filterType, setFilterType] = useState('all');
    const [filterBrand, setFilterBrand] = useState('all');
    const [searchLocal, setSearchLocal] = useState('');
    const [sortBy, setSortBy] = useState('newest');

    const pool = vehicles.filter(v => v.category === 'Voiture' || v.category === 'Moto');
    const brandsAvailable = [...new Set(
      pool.filter(v => filterType === 'all' || v.category === filterType).map(v => v.brand)
    )].sort();

    useEffect(() => {
      if (filterBrand !== 'all' && !brandsAvailable.includes(filterBrand)) setFilterBrand('all');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterType]);

    const filtered = pool
      .filter(v => filterType === 'all' || v.category === filterType)
      .filter(v => filterBrand === 'all' || v.brand === filterBrand)
      .filter(v => !searchLocal || `${v.name} ${v.brand}`.toLowerCase().includes(searchLocal.toLowerCase()))
      .sort((a, b) => {
        if (sortBy === 'price-asc') return (a.price || 0) - (b.price || 0);
        if (sortBy === 'price-desc') return (b.price || 0) - (a.price || 0);
        return b.year - a.year;
      });

    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        {/* Hero header */}
        <div className="bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white py-10 sm:py-16 mb-8">
          <div className="max-w-7xl mx-auto px-4">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 mb-8 text-gray-500 hover:text-red-500 transition-colors uppercase text-[10px] font-black tracking-widest group">
              <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Accueil
            </button>
            <div className="flex items-end gap-6 mb-6">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-600 mb-2">— Catalogue</p>
                <h2 className="text-4xl sm:text-6xl font-black italic uppercase tracking-tighter leading-none">
                  Automobiles
                </h2>
                <p className="text-gray-500 font-bold mt-2 text-sm">{filtered.length} véhicule{filtered.length > 1 ? 's' : ''} disponible{filtered.length > 1 ? 's' : ''}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="bg-white/5 border border-white/10 px-4 py-2 rounded-xl flex items-center gap-2">
                <Car size={16} className="text-red-500" />
                <span className="text-white font-black text-lg leading-none">{pool.filter(v => v.category === 'Voiture').length}</span>
                <span className="text-gray-500 text-xs font-bold uppercase tracking-widest">Voitures</span>
              </div>
              <div className="bg-white/5 border border-white/10 px-4 py-2 rounded-xl flex items-center gap-2">
                <span className="text-orange-400 font-black text-lg leading-none">{pool.filter(v => v.category === 'Moto').length}</span>
                <span className="text-gray-500 text-xs font-bold uppercase tracking-widest">Motos</span>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 flex flex-col lg:flex-row gap-8">
          {/* Sidebar filtres */}
          <div className="w-full lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 lg:sticky lg:top-24">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-black italic uppercase text-gray-900 tracking-tighter">Filtres</h3>
                {(filterType !== 'all' || filterBrand !== 'all' || searchLocal) && (
                  <button onClick={() => { setFilterType('all'); setFilterBrand('all'); setSearchLocal(''); }}
                    className="text-[10px] font-black text-red-600 uppercase tracking-widest hover:underline">
                    Effacer
                  </button>
                )}
              </div>

              <div className="mb-6">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 block">Recherche</label>
                <input type="text" placeholder="Modèle, version..."
                  value={searchLocal} onChange={e => setSearchLocal(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-red-600 outline-none transition-all" />
              </div>

              <div className="mb-6">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 block">Type</label>
                <div className="space-y-1.5">
                  {[{ key: 'all', label: 'Tous' }, { key: 'Voiture', label: 'Voitures' }, { key: 'Moto', label: 'Motos' }]
                    .map(({ key, label }) => (
                      <button key={key} onClick={() => setFilterType(key)}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all text-sm font-black uppercase tracking-widest ${filterType === key ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}>
                        {label}
                        {filterType === key && <span className="w-2 h-2 rounded-full bg-red-600 flex-shrink-0" />}
                      </button>
                    ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 block">Marque</label>
                <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
                  <button onClick={() => setFilterBrand('all')}
                    className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest ${filterBrand === 'all' ? 'bg-red-50 text-red-600' : 'text-gray-500 hover:bg-gray-50'}`}>
                    Toutes <span className="text-[10px] bg-white px-2 py-0.5 rounded-full">{brandsAvailable.length}</span>
                  </button>
                  {brandsAvailable.map(brand => {
                    const count = pool.filter(v => v.brand === brand && (filterType === 'all' || v.category === filterType)).length;
                    return (
                      <button key={brand} onClick={() => setFilterBrand(brand)}
                        className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest ${filterBrand === brand ? 'bg-red-50 text-red-600' : 'text-gray-500 hover:bg-gray-50'}`}>
                        {brand}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${filterBrand === brand ? 'bg-white text-red-600' : 'bg-gray-100 text-gray-400'}`}>{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Grille résultats */}
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 bg-white p-4 sm:px-6 rounded-2xl shadow-sm border border-gray-100">
              <p className="text-gray-500 font-bold text-sm">
                <span className="font-black text-gray-900">{filtered.length}</span> véhicule{filtered.length > 1 ? 's' : ''}
              </p>
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Trier</span>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                  className="bg-gray-50 border-none rounded-xl py-2.5 px-4 text-xs font-black uppercase tracking-widest text-gray-700 focus:ring-2 focus:ring-red-600 outline-none cursor-pointer">
                  <option value="newest">Récents</option>
                  <option value="price-asc">Prix ↑</option>
                  <option value="price-desc">Prix ↓</option>
                </select>
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
                <p className="text-5xl font-black italic uppercase text-gray-100 mb-3">Aucun résultat</p>
                <p className="text-gray-500 font-bold mb-8">Aucun véhicule ne correspond à vos critères.</p>
                <button onClick={() => { setFilterType('all'); setFilterBrand('all'); setSearchLocal(''); }}
                  className="px-8 py-4 bg-red-600 text-white rounded-xl font-black uppercase text-xs tracking-widest hover:bg-gray-900 transition-all">
                  Réinitialiser
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {filtered.map(v => (
                  <PremiumVehicleCard key={v._id} vehicle={v} accentColor={v.category === 'Moto' ? 'orange' : 'red'} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ══════════════════════════════════════════════════════════════════════
  // 🚤 BATEAUX PAGE
  // ══════════════════════════════════════════════════════════════════════
  const BateauxPage = () => {
    const [searchLocal, setSearchLocal] = useState('');
    const pool = vehicles.filter(v => v.category === 'Bateau');
    const filtered = pool.filter(v =>
      !searchLocal || `${v.name} ${v.brand}`.toLowerCase().includes(searchLocal.toLowerCase())
    );

    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <div className="bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white py-10 sm:py-16 mb-8">
          <div className="max-w-7xl mx-auto px-4">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 mb-8 text-gray-500 hover:text-red-500 transition-colors uppercase text-[10px] font-black tracking-widest group">
              <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Accueil
            </button>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-600 mb-2">— Catalogue</p>
              <h2 className="text-4xl sm:text-6xl font-black italic uppercase tracking-tighter leading-none">Bateaux</h2>
              <p className="text-gray-500 font-bold mt-2 text-sm">{pool.length} embarcation{pool.length > 1 ? 's' : ''} disponible{pool.length > 1 ? 's' : ''}</p>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 flex flex-col lg:flex-row gap-8">
          <div className="w-full lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 lg:sticky lg:top-24">
              <h3 className="font-black italic uppercase text-gray-900 tracking-tighter mb-4">Recherche</h3>
              <input type="text" placeholder="Modèle, marque..."
                value={searchLocal} onChange={e => setSearchLocal(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-all" />
            </div>
          </div>
          <div className="flex-1">
            <div className="flex justify-between items-center mb-6 bg-white p-4 px-6 rounded-2xl shadow-sm border border-gray-100">
              <p className="text-gray-500 font-bold text-sm"><span className="font-black text-gray-900">{filtered.length}</span> embarcation{filtered.length > 1 ? 's' : ''}</p>
            </div>
            {filtered.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
                <p className="text-5xl font-black italic uppercase text-gray-100 mb-3">Aucun résultat</p>
                <button onClick={() => setSearchLocal('')} className="px-8 py-4 bg-blue-600 text-white rounded-xl font-black uppercase text-xs tracking-widest hover:bg-gray-900 transition-all mt-4">Réinitialiser</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {filtered.map(vehicle => (
                  <div key={vehicle._id} onClick={() => { setSelectedVehicle(vehicle); navigate('/detail'); }}
                    className="group bg-white rounded-2xl overflow-hidden border-2 border-gray-100 group-hover:border-blue-400 hover:shadow-2xl transition-all duration-500 cursor-pointer relative flex flex-col">
                    <div className="h-52 overflow-hidden relative bg-gradient-to-br from-blue-950 to-blue-900 flex-shrink-0">
                      {vehicle.images?.[0]
                        ? <img src={vehicle.images[0]} alt={vehicle.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" onError={e => { e.target.src = 'https://via.placeholder.com/400x300/1e3a5f/6b9fd4?text=Photo'; }} />
                        : <div className="w-full h-full flex items-center justify-center"><Anchor size={48} className="text-blue-700 opacity-30" /></div>
                      }
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="bg-white text-gray-900 px-6 py-2 rounded-full font-black uppercase text-[10px] tracking-widest translate-y-4 group-hover:translate-y-0 transition-transform">Voir la fiche</span>
                      </div>
                      <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-2.5 py-1 rounded-lg shadow-sm">
                        <span className="text-[10px] font-black uppercase tracking-widest text-gray-900">{vehicle.year}</span>
                      </div>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600 mb-1">{vehicle.brand}</span>
                      <h3 className="font-black text-gray-900 text-sm leading-tight mb-4 line-clamp-1 flex-1">{vehicle.name}</h3>
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {vehicle.length && <span className="flex items-center gap-1 text-[10px] bg-gray-50 border border-gray-100 text-gray-600 font-black uppercase tracking-widest px-2 py-1 rounded-lg"><ArrowRight size={10} /> {vehicle.length}</span>}
                        {vehicle.power && <span className="flex items-center gap-1 text-[10px] bg-gray-50 border border-gray-100 text-gray-600 font-black uppercase tracking-widest px-2 py-1 rounded-lg"><Settings size={10} /> {vehicle.power.split('(')[0].trim()}</span>}
                        {vehicle.seats && <span className="text-[10px] bg-gray-50 border border-gray-100 text-gray-600 font-black uppercase tracking-widest px-2 py-1 rounded-lg">{vehicle.seats}p</span>}
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <div>
                          <p className="text-[9px] text-gray-400 font-black uppercase tracking-[0.2em] mb-0.5">Prix Import</p>
                          <p className="font-black text-gray-900 text-base leading-none">{formatPrice(vehicle.price)}</p>
                        </div>
                        <div className="w-9 h-9 rounded-full bg-gray-900 group-hover:bg-blue-600 flex items-center justify-center transition-colors duration-300 shadow-md flex-shrink-0">
                          <ChevronRight size={16} className="text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ══════════════════════════════════════════════════════════════════════
  // 🏡 BUNGALOWS PAGE
  // ══════════════════════════════════════════════════════════════════════
  const BungalowsPage = () => {
    const [searchLocal, setSearchLocal] = useState('');
    const pool = vehicles.filter(v => v.category === 'Bungalow');
    const filtered = pool.filter(v =>
      !searchLocal || `${v.name} ${v.brand}`.toLowerCase().includes(searchLocal.toLowerCase())
    );

    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <div className="bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white py-10 sm:py-16 mb-8">
          <div className="max-w-7xl mx-auto px-4">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 mb-8 text-gray-500 hover:text-red-500 transition-colors uppercase text-[10px] font-black tracking-widest group">
              <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Accueil
            </button>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-600 mb-2">— Catalogue</p>
              <h2 className="text-4xl sm:text-6xl font-black italic uppercase tracking-tighter leading-none">Bungalows</h2>
              <p className="text-gray-500 font-bold mt-2 text-sm">{pool.length} bungalow{pool.length > 1 ? 's' : ''} disponible{pool.length > 1 ? 's' : ''}</p>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 flex flex-col lg:flex-row gap-8">
          <div className="w-full lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 lg:sticky lg:top-24">
              <h3 className="font-black italic uppercase text-gray-900 tracking-tighter mb-4">Recherche</h3>
              <input type="text" placeholder="Modèle, caractéristique..."
                value={searchLocal} onChange={e => setSearchLocal(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-amber-500 outline-none transition-all" />
            </div>
          </div>
          <div className="flex-1">
            <div className="flex justify-between items-center mb-6 bg-white p-4 px-6 rounded-2xl shadow-sm border border-gray-100">
              <p className="text-gray-500 font-bold text-sm"><span className="font-black text-gray-900">{filtered.length}</span> bungalow{filtered.length > 1 ? 's' : ''}</p>
            </div>
            {filtered.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
                <p className="text-5xl font-black italic uppercase text-gray-100 mb-3">Aucun résultat</p>
                <button onClick={() => setSearchLocal('')} className="px-8 py-4 bg-amber-500 text-white rounded-xl font-black uppercase text-xs tracking-widest hover:bg-gray-900 transition-all mt-4">Réinitialiser</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {filtered.map(vehicle => (
                  <div key={vehicle._id} onClick={() => { setSelectedVehicle(vehicle); navigate('/detail'); }}
                    className="group bg-white rounded-2xl overflow-hidden border-2 border-gray-100 hover:border-amber-400 hover:shadow-2xl transition-all duration-500 cursor-pointer relative flex flex-col">
                    <div className="h-52 overflow-hidden relative bg-gradient-to-br from-amber-950 to-amber-900 flex-shrink-0">
                      {vehicle.images?.[0]
                        ? <img src={vehicle.images[0]} alt={vehicle.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" onError={e => { e.target.src = 'https://via.placeholder.com/400x300/78350f/d97706?text=Photo'; }} />
                        : <div className="w-full h-full flex items-center justify-center"><HomeIcon size={48} className="text-amber-700 opacity-30" /></div>
                      }
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="bg-white text-gray-900 px-6 py-2 rounded-full font-black uppercase text-[10px] tracking-widest translate-y-4 group-hover:translate-y-0 transition-transform">Voir la fiche</span>
                      </div>
                      <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-2.5 py-1 rounded-lg shadow-sm">
                        <span className="text-[10px] font-black uppercase tracking-widest text-gray-900">{vehicle.year}</span>
                      </div>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-600 mb-1">{vehicle.brand}</span>
                      <h3 className="font-black text-gray-900 text-sm leading-tight mb-4 line-clamp-1 flex-1">{vehicle.name}</h3>
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {vehicle.surface && <span className="text-[10px] bg-amber-50 border border-amber-100 text-amber-700 font-black uppercase tracking-widest px-2 py-1 rounded-lg">{vehicle.surface}</span>}
                        {vehicle.capacite && <span className="text-[10px] bg-gray-50 border border-gray-100 text-gray-600 font-black uppercase tracking-widest px-2 py-1 rounded-lg">{vehicle.capacite}</span>}
                        {vehicle.seats && <span className="text-[10px] bg-gray-50 border border-gray-100 text-gray-600 font-black uppercase tracking-widest px-2 py-1 rounded-lg">{vehicle.seats}p</span>}
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <div>
                          <p className="text-[9px] text-gray-400 font-black uppercase tracking-[0.2em] mb-0.5">Prix Import</p>
                          <p className="font-black text-gray-900 text-base leading-none">{vehicle.price && vehicle.price > 1000 ? formatPrice(vehicle.price) : 'Sur demande'}</p>
                        </div>
                        <div className="w-9 h-9 rounded-full bg-gray-900 group-hover:bg-amber-500 flex items-center justify-center transition-colors duration-300 shadow-md flex-shrink-0">
                          <ChevronRight size={16} className="text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // 🎨 STOCKPAGE — Premium
  const StockPage = () => {
    const ALL_CATS = ['Voiture', 'Moto', 'Bateau', 'Bungalow'];
    const [activeCats, setActiveCats] = useState(new Set(ALL_CATS));
    const [search, setSearch] = useState('');
    const [filterFuel, setFilterFuel] = useState('all');
    const [filterBrand, setFilterBrand] = useState('all');
    const [sortBy, setSortBy] = useState('newest');
    const [viewMode, setViewMode] = useState('grid');

    const CAT_CONFIG = {
      Voiture:  { label: 'Voitures',  bar: 'bg-red-500',                               pill: 'bg-red-50 text-red-700 border-red-100',    border: 'border-red-500' },
      Moto:     { label: 'Motos',     bar: 'bg-gradient-to-r from-orange-400 to-red-500', pill: 'bg-orange-50 text-orange-700 border-orange-100', border: 'border-orange-400' },
      Bateau:   { label: 'Bateaux',   bar: 'bg-gradient-to-r from-blue-400 to-cyan-500',  pill: 'bg-blue-50 text-blue-700 border-blue-100',     border: 'border-blue-400' },
      Bungalow: { label: 'Bungalows', bar: 'bg-gradient-to-r from-amber-400 to-orange-500', pill: 'bg-amber-50 text-amber-700 border-amber-100',   border: 'border-amber-400' },
    };

    const fuelGroup = (fuel) => {
      if (!fuel) return null;
      const f = fuel.toLowerCase();
      if (f.includes('électr') || f.includes('electr')) return 'Électrique';
      if (f.includes('phev') || f.includes('hybride') || f.includes('hybrid')) return 'Hybride';
      if (f.includes('diesel')) return 'Diesel';
      if (f.includes('essence')) return 'Essence';
      return null;
    };

    const toggleCat = (key) => {
      setActiveCats(prev => {
        const next = new Set(prev);
        if (next.has(key)) { if (next.size > 1) next.delete(key); }
        else next.add(key);
        setFilterBrand('all');
        return next;
      });
    };

    const brandsAvailable = [...new Set(
      vehicles.filter(v => activeCats.has(v.category)).map(v => v.brand).filter(Boolean)
    )].sort();

    const filtered = vehicles
      .filter(v => activeCats.has(v.category))
      .filter(v => filterBrand === 'all' || v.brand === filterBrand)
      .filter(v => filterFuel === 'all' || fuelGroup(v.fuel) === filterFuel)
      .filter(v => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return [v.name, v.brand, v.category].some(s => s?.toLowerCase().includes(q));
      })
      .sort((a, b) => {
        if (sortBy === 'price-asc') return (a.price || 0) - (b.price || 0);
        if (sortBy === 'price-desc') return (b.price || 0) - (a.price || 0);
        return (b.year || 0) - (a.year || 0);
      });

    const activeFiltersCount = [
      filterBrand !== 'all',
      filterFuel !== 'all',
      search.trim() !== '',
      activeCats.size < ALL_CATS.length,
    ].filter(Boolean).length;

    const resetAll = () => {
      setActiveCats(new Set(ALL_CATS));
      setSearch('');
      setFilterFuel('all');
      setFilterBrand('all');
      setSortBy('newest');
    };

    // Carte grille — premium sans emoji
    const GridCard = ({ v }) => {
      const cfg = CAT_CONFIG[v.category] || CAT_CONFIG.Voiture;
      return (
        <div onClick={() => { setSelectedVehicle(v); navigate('/detail'); }}
          className={`group bg-white rounded-2xl overflow-hidden border-2 border-gray-100 hover:${cfg.border} hover:shadow-2xl transition-all duration-300 cursor-pointer flex flex-col`}>
          <div className={`h-0.5 ${cfg.bar}`} />
          <div className={`relative overflow-hidden ${v.category === 'Moto' ? 'bg-gray-900' : v.category === 'Bateau' ? 'bg-blue-950' : 'bg-gray-50'}`} style={{ height: '190px' }}>
            {v.images?.[0] ? (
              <img src={v.images[0]} alt={v.name} loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                onError={e => { e.target.src = 'https://via.placeholder.com/400x300/f3f4f6/9ca3af?text=Photo'; }} />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-1 opacity-20">
                <Car size={40} />
              </div>
            )}
            <div className="absolute top-3 left-3">
              <span className="bg-white/95 backdrop-blur text-gray-900 text-[9px] font-black px-2 py-1 rounded-lg shadow">{v.year}</span>
            </div>
            <div className="absolute top-3 right-3">
              <span className={`text-[9px] font-black uppercase px-2 py-1 rounded-lg border ${cfg.pill}`}>{v.category}</span>
            </div>
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="bg-white text-gray-900 text-[9px] font-black uppercase tracking-widest px-4 py-2 rounded-full translate-y-2 group-hover:translate-y-0 transition-transform">Voir la fiche</span>
            </div>
          </div>
          <div className="p-4 flex flex-col flex-1">
            <p className="text-[9px] font-black uppercase tracking-[0.2em] text-red-600 mb-0.5">{v.brand}</p>
            <h3 className="font-black text-gray-900 text-sm leading-tight mb-3 line-clamp-2 flex-1">{v.name}</h3>
            {/* Badges Lucide — sans emoji */}
            <div className="flex flex-wrap gap-1 mb-3">
              {v.fuel && fuelGroup(v.fuel) && (
                <span className="flex items-center gap-1 text-[9px] bg-gray-50 border border-gray-100 text-gray-600 font-black uppercase tracking-widest px-2 py-1 rounded-lg">
                  <Fuel size={9} className="flex-shrink-0" /> {fuelGroup(v.fuel)}
                </span>
              )}
              {v.power && (
                <span className="flex items-center gap-1 text-[9px] bg-gray-50 border border-gray-100 text-gray-600 font-black uppercase tracking-widest px-2 py-1 rounded-lg">
                  <Settings size={9} className="flex-shrink-0" /> {v.power.split(' ')[0]} ch
                </span>
              )}
              {v.seats && (
                <span className="text-[9px] bg-gray-50 border border-gray-100 text-gray-600 font-black uppercase tracking-widest px-2 py-1 rounded-lg">{v.seats}p</span>
              )}
              {v.length && (
                <span className="text-[9px] bg-blue-50 border border-blue-100 text-blue-600 font-black uppercase tracking-widest px-2 py-1 rounded-lg">{v.length}</span>
              )}
              {v.surface && (
                <span className="text-[9px] bg-amber-50 border border-amber-100 text-amber-700 font-black uppercase tracking-widest px-2 py-1 rounded-lg">{v.surface}</span>
              )}
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <div>
                <p className="text-[9px] text-gray-400 font-black uppercase tracking-[0.2em]">Prix import</p>
                <p className="font-black text-gray-900 text-sm">{v.price && (v.category !== 'Bungalow' || v.price > 1000) ? formatPrice(v.price) : 'Sur demande'}</p>
              </div>
              <div className="w-8 h-8 rounded-full bg-gray-900 group-hover:bg-red-600 flex items-center justify-center transition-colors duration-300">
                <ChevronRight size={14} className="text-white" />
              </div>
            </div>
          </div>
        </div>
      );
    };

    // Carte liste — premium sans emoji
    const ListCard = ({ v }) => {
      const cfg = CAT_CONFIG[v.category] || CAT_CONFIG.Voiture;
      return (
        <div onClick={() => { setSelectedVehicle(v); navigate('/detail'); }}
          className={`group bg-white rounded-xl border-2 border-gray-100 hover:${cfg.border} hover:shadow-lg transition-all duration-200 cursor-pointer flex items-center gap-4 p-3`}>
          <div className={`w-24 h-16 flex-shrink-0 rounded-lg overflow-hidden ${v.category === 'Moto' ? 'bg-gray-900' : v.category === 'Bateau' ? 'bg-blue-950' : 'bg-gray-50'}`}>
            {v.images?.[0] ? (
              <img src={v.images[0]} alt={v.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                onError={e => { e.target.src = 'https://via.placeholder.com/200x150/f3f4f6/9ca3af?text=Photo'; }} />
            ) : (
              <div className="w-full h-full flex items-center justify-center opacity-20"><Car size={24} /></div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-[9px] font-black uppercase tracking-[0.2em] text-red-600">{v.brand}</span>
              <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded border ${cfg.pill}`}>{v.category}</span>
            </div>
            <p className="font-black text-gray-900 text-sm truncate">{v.name}</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {v.year && <span className="text-[8px] text-gray-400 font-black uppercase tracking-widest">{v.year}</span>}
              {v.fuel && fuelGroup(v.fuel) && (
                <span className="flex items-center gap-0.5 text-[8px] bg-gray-100 text-gray-500 font-black px-1.5 py-0.5 rounded">
                  <Fuel size={8} /> {fuelGroup(v.fuel)}
                </span>
              )}
              {v.power && (
                <span className="flex items-center gap-0.5 text-[8px] bg-gray-100 text-gray-500 font-black px-1.5 py-0.5 rounded">
                  <Settings size={8} /> {v.power.split(' ')[0]} ch
                </span>
              )}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="font-black text-gray-900 text-sm">{v.price && (v.category !== 'Bungalow' || v.price > 1000) ? formatPrice(v.price) : 'Sur demande'}</p>
            <ChevronRight size={16} className="text-gray-300 group-hover:text-red-600 transition-colors ml-auto mt-1" />
          </div>
        </div>
      );
    };

    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <div className="bg-gray-950 text-white pt-10 pb-8 sm:pt-14 sm:pb-10">
          <div className="max-w-7xl mx-auto px-4">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 mb-6 text-gray-600 hover:text-red-400 transition-colors uppercase text-[10px] font-black tracking-widest group">
              <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Accueil
            </button>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-8">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-600 mb-2">— Catalogue complet</p>
                <h2 className="text-4xl sm:text-6xl font-black italic uppercase tracking-tighter leading-none">
                  Notre <span className="text-red-500">Stock</span>
                </h2>
                <p className="text-gray-500 font-bold mt-2 text-sm">
                  <span className="text-white font-black text-lg">{filtered.length}</span> produit{filtered.length > 1 ? 's' : ''}
                  <span className="text-gray-700 mx-2">·</span>
                  {vehicles.length} au catalogue
                </p>
              </div>
              <div className="relative w-full sm:w-80">
                <input type="text" placeholder="Marque, modèle, catégorie..."
                  value={search} onChange={e => setSearch(e.target.value)}
                  className="w-full pl-4 pr-10 py-3 bg-white/10 border border-white/15 rounded-xl text-sm text-white placeholder-gray-600 focus:outline-none focus:border-red-500 focus:bg-white/15 transition-all font-bold" />
                {search && <button onClick={() => setSearch('')} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white font-black text-xs">✕</button>}
              </div>
            </div>

            {/* Compteurs catégories */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {ALL_CATS.map(key => {
                const cfg = CAT_CONFIG[key];
                const count = vehicles.filter(v => v.category === key).length;
                const isActive = activeCats.has(key);
                return (
                  <button key={key} onClick={() => toggleCat(key)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all text-left ${isActive ? 'bg-white/10 border-white/20' : 'bg-white/5 border-white/5 opacity-40'}`}>
                    <div>
                      <p className="text-white font-black text-xl leading-none">{count}</p>
                      <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mt-0.5">{cfg.label}</p>
                    </div>
                    {isActive && <div className="ml-auto w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Barre filtres sticky */}
        <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
              {activeFiltersCount > 0 && (
                <button onClick={resetAll}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 bg-red-600 text-white rounded-xl font-black uppercase text-[9px] tracking-widest hover:bg-red-700 transition-all">
                  <X size={10} /> Reset ({activeFiltersCount})
                </button>
              )}
              <div className="w-px h-5 bg-gray-200 flex-shrink-0" />
              {[
                { k: 'all',       l: 'Tous',        style: filterFuel === 'all'        ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200' },
                { k: 'Essence',   l: 'Essence',     style: filterFuel === 'Essence'    ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-orange-50' },
                { k: 'Diesel',    l: 'Diesel',      style: filterFuel === 'Diesel'     ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200' },
                { k: 'Électrique',l: 'Électrique',  style: filterFuel === 'Électrique' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-blue-50' },
                { k: 'Hybride',   l: 'Hybride',     style: filterFuel === 'Hybride'    ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-green-50' },
              ].map(({ k, l, style }) => (
                <button key={k} onClick={() => setFilterFuel(k)}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl font-black uppercase text-[9px] tracking-widest transition-all ${style}`}>
                  {k !== 'all' && <Fuel size={10} />} {l}
                </button>
              ))}
              <div className="w-px h-5 bg-gray-200 flex-shrink-0" />
              <select value={filterBrand} onChange={e => setFilterBrand(e.target.value)}
                className={`flex-shrink-0 border-2 rounded-xl py-2 px-3 text-[9px] font-black uppercase tracking-widest focus:ring-2 focus:ring-red-500 outline-none cursor-pointer transition-all ${filterBrand !== 'all' ? 'border-red-400 bg-red-50 text-red-700' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
                <option value="all">Toutes marques</option>
                {brandsAvailable.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                className="flex-shrink-0 bg-gray-50 border-2 border-gray-200 rounded-xl py-2 px-3 text-[9px] font-black uppercase tracking-widest text-gray-600 focus:ring-2 focus:ring-red-500 outline-none cursor-pointer">
                <option value="newest">Récents</option>
                <option value="price-asc">Prix ↑</option>
                <option value="price-desc">Prix ↓</option>
              </select>
              <div className="flex-shrink-0 flex items-center bg-gray-100 rounded-xl p-1 ml-auto">
                <button onClick={() => setViewMode('grid')}
                  className={`px-3 py-1.5 rounded-lg font-black text-[9px] uppercase tracking-widest transition-all ${viewMode === 'grid' ? 'bg-white shadow text-gray-900' : 'text-gray-400'}`}>
                  Grille
                </button>
                <button onClick={() => setViewMode('list')}
                  className={`px-3 py-1.5 rounded-lg font-black text-[9px] uppercase tracking-widest transition-all ${viewMode === 'list' ? 'bg-white shadow text-gray-900' : 'text-gray-400'}`}>
                  Liste
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tags filtres actifs */}
        {activeFiltersCount > 0 && (
          <div className="max-w-7xl mx-auto px-4 pt-4 flex flex-wrap gap-2 items-center">
            {activeCats.size < ALL_CATS.length && [...activeCats].map(c => (
              <span key={c} className={`flex items-center gap-1 text-[9px] font-black uppercase px-3 py-1.5 rounded-full border ${CAT_CONFIG[c].pill}`}>
                {CAT_CONFIG[c].label}
                <button onClick={() => toggleCat(c)} className="ml-1 opacity-60 hover:opacity-100">✕</button>
              </span>
            ))}
            {filterBrand !== 'all' && (
              <span className="flex items-center gap-1 text-[9px] font-black uppercase px-3 py-1.5 rounded-full bg-gray-900 text-white">
                {filterBrand} <button onClick={() => setFilterBrand('all')} className="ml-1 opacity-60 hover:opacity-100">✕</button>
              </span>
            )}
            {filterFuel !== 'all' && (
              <span className="flex items-center gap-1 text-[9px] font-black uppercase px-3 py-1.5 rounded-full bg-gray-900 text-white">
                {filterFuel} <button onClick={() => setFilterFuel('all')} className="ml-1 opacity-60 hover:opacity-100">✕</button>
              </span>
            )}
            {search && (
              <span className="flex items-center gap-1 text-[9px] font-black uppercase px-3 py-1.5 rounded-full bg-gray-900 text-white">
                "{search}" <button onClick={() => setSearch('')} className="ml-1 opacity-60 hover:opacity-100">✕</button>
              </span>
            )}
            <span className="text-gray-400 text-[9px] font-bold ml-1">{filtered.length} résultat{filtered.length > 1 ? 's' : ''}</span>
          </div>
        )}

        {/* Grille / Liste */}
        <div className="max-w-7xl mx-auto px-4 pt-6">
          {filtered.length === 0 ? (
            <div className="text-center py-28">
              <p className="text-5xl font-black italic uppercase text-gray-200 mb-3">Aucun résultat</p>
              <p className="text-sm text-gray-500 font-bold mb-8">Essayez d'autres filtres</p>
              <button onClick={resetAll}
                className="px-8 py-3 bg-red-600 text-white font-black rounded-xl hover:bg-red-700 transition-all uppercase text-xs tracking-widest shadow-lg">
                Réinitialiser
              </button>
            </div>
          ) : viewMode === 'list' ? (
            <div className="space-y-2">{filtered.map(v => <ListCard key={v._id} v={v} />)}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filtered.map(v => <GridCard key={v._id} v={v} />)}
            </div>
          )}
        </div>
      </div>
    );
  };

  const CartModal = () => (
    <div className="fixed inset-0 flex items-center justify-center z-[100] p-4">
      {/* Overlay avec flou léger comme les autres modales */}
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-[3px] animate-in fade-in duration-300"
        onClick={() => setShowCart(false)}
      />

      {/* Conteneur principal */}
      <div className="relative bg-white rounded-2xl sm:rounded-[2.5rem] max-w-lg sm:max-w-2xl w-full max-h-[90vh] sm:max-h-[85vh] overflow-hidden flex flex-col shadow-[0_20px_50px_rgba(0,0,0,0.3)] border border-white/20 animate-in zoom-in-95 duration-200">

        {/* Header Racing */}
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 p-6 text-white relative border-b border-red-600/30">
          <div className="flex justify-between items-center px-2">
            <div>
              <h2 className="text-3xl font-black italic uppercase tracking-tighter">
                Mon <span className="text-red-600">Panier</span>
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="w-2 h-2 bg-red-600 rounded-full animate-pulse" />
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">
                  {cart.length} véhicule{cart.length > 1 ? 's' : ''} sélectionné{cart.length > 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowCart(false)}
              className="group p-2 hover:bg-white/10 rounded-full transition-all duration-300"
            >
              <X size={28} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
            </button>
          </div>
        </div>

        {/* Liste des articles */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
          {cart.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-6xl mb-4 opacity-20 italic font-black text-gray-400 uppercase select-none">Vide</div>
              <ShoppingCart size={80} className="mx-auto text-gray-200 mb-6" />
              <p className="text-gray-500 font-black uppercase italic tracking-widest text-sm">{t.emptyCart}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map(item => (
                <div key={item.vehicleId} className="flex gap-5 p-4 bg-white rounded-3xl border border-gray-100 hover:border-red-600/20 hover:shadow-xl transition-all duration-300 group">
                  {/* Image avec Overlay "Détails" au survol */}
                  <div className="w-28 h-20 bg-gray-100 rounded-2xl flex-shrink-0 relative overflow-hidden shadow-inner border border-gray-50">
                    {item.images && item.images[0] ? (
                      <img
                        src={item.images[0]}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl opacity-30">🚗</div>
                    )}
                  </div>

                  <div className="flex-1 py-1">
                    <h3 className="font-black text-gray-900 uppercase italic text-sm tracking-tight leading-none mb-1">{item.name}</h3>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">{item.brand} • {item.year}</p>
                    <p className="text-lg font-black text-red-600 tracking-tighter italic">{formatPrice(item.price)}</p>
                  </div>

                  {/* Contrôles de quantité stylisés */}
                  <div className="flex flex-col items-end justify-between py-1">
                    <button
                      onClick={() => removeFromCart(item.vehicleId)}
                      className="p-2 text-gray-300 hover:text-red-600 transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                    <div className="flex items-center gap-1 bg-gray-900 text-white rounded-xl p-1 shadow-lg scale-90">
                      <button onClick={() => updateQuantity(item.vehicleId, -1)} className="p-1.5 hover:bg-red-600 rounded-lg transition-colors">
                        <Minus size={14} strokeWidth={3} />
                      </button>
                      <span className="w-8 text-center font-black italic text-xs tracking-tighter">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.vehicleId, 1)} className="p-1.5 hover:bg-red-600 rounded-lg transition-colors">
                        <Plus size={14} strokeWidth={3} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer Résumé */}
        {cart.length > 0 && (
          <div className="p-4 sm:p-8 border-t border-gray-100 bg-white">
            <div className="space-y-3 mb-6 px-2">
              <div className="flex justify-between text-[10px] font-black uppercase tracking-widest text-gray-400">
                <span>Sous-total</span>
                <span className="text-gray-900">{formatPrice(cartTotal)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-t border-dashed border-gray-200 pt-4">
                <span className="text-sm font-black italic uppercase tracking-tighter text-gray-900">Total estimé</span>
                <span className="text-3xl font-black italic text-red-600 tracking-tighter">{formatPrice(cartTotal)}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* WhatsApp - Design aligné sur ContactModal */}
              <button
                onClick={exportToWhatsApp}
                className="group relative flex items-center justify-center gap-3 bg-white border-2 border-green-500 text-green-600 py-4 rounded-2xl font-black uppercase italic text-xs tracking-widest hover:bg-green-600 hover:text-white transition-all duration-300 shadow-lg"
              >
                <Send size={18} strokeWidth={3} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                WhatsApp
              </button>

              {/* Télécharger - Design aligné sur le reste */}
              <button
                onClick={downloadOrder}
                className="group relative flex items-center justify-center gap-3 bg-gray-900 text-white py-4 rounded-2xl font-black uppercase italic text-xs tracking-widest hover:bg-red-600 transition-all duration-300 shadow-lg"
              >
                <Download size={18} strokeWidth={3} className="group-hover:translate-y-1 transition-transform" />
                PDF
              </button>
            </div>

            <p className="text-[8px] text-center text-gray-400 font-black uppercase tracking-[0.3em] mt-6">
              Technic Import · Importation Directe · Solutions de Financement
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // Modal Contact
  const ContactModal = () => (
    <div className="fixed inset-0 flex items-center justify-center z-[100] p-3 sm:p-4">
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-[3px]"
        onClick={() => setShowContactModal(false)}
      />

      {/* max-w-sm sur mobile, max-w-md sur desktop */}
      <div className="relative bg-white rounded-3xl sm:rounded-[2.5rem] max-w-sm sm:max-w-md w-full overflow-hidden shadow-2xl">

        {/* Header - padding réduit sur mobile */}
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 p-4 sm:p-7 text-white relative">
          <div className="absolute top-0 right-0 p-3 sm:p-5">
            <button
              onClick={() => setShowContactModal(false)}
              className="p-1.5 sm:p-2 hover:bg-white/10 rounded-full transition-all"
            >
              <X size={20} className="sm:w-6 sm:h-6" strokeWidth={3} />
            </button>
          </div>
          <h2 className="text-lg sm:text-2xl font-black italic uppercase tracking-tighter pr-10">
            Contactez <span className="text-red-600">Technic Import</span>
          </h2>
          <div className="h-1 w-10 sm:w-12 bg-red-600 mt-2 rounded-full" />
        </div>

        {/* Body - padding et spacing adaptés */}
        <div className="p-4 sm:p-7 space-y-3 sm:space-y-4 bg-white">
          <p className="text-gray-400 text-[10px] sm:text-xs uppercase font-black tracking-wider ml-1">
            Canaux prioritaires
          </p>

          {/* WhatsApp - tailles adaptatives */}
          <button
            onClick={() => window.open('https://wa.me/33601905164')}
            className="w-full flex items-center gap-3 sm:gap-5 p-3.5 sm:p-5 bg-white border-2 border-green-500/20 hover:border-green-500 rounded-2xl sm:rounded-3xl transition-all group hover:-translate-y-1 hover:shadow-xl relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 bg-green-500 text-white text-[9px] sm:text-[10px] font-black uppercase px-2 sm:px-3 py-0.5 sm:py-1 rounded-bl-xl tracking-wider">
              Recommandé
            </div>

            <div className="relative flex-shrink-0">
              <div className="absolute inset-0 bg-green-500 blur-xl opacity-20 group-hover:opacity-40 transition-opacity" />
              <div className="relative w-10 h-10 sm:w-12 sm:h-12 bg-green-50 text-green-600 rounded-xl sm:rounded-2xl flex items-center justify-center group-hover:bg-green-600 group-hover:text-white group-hover:rotate-12 transition-all">
                <MessageCircle size={20} className="sm:w-6 sm:h-6" strokeWidth={2.5} />
              </div>
            </div>

            <div className="text-left min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="font-black text-gray-900 uppercase italic text-xs sm:text-sm group-hover:text-green-600 transition-colors truncate">
                  WhatsApp
                </div>
                <span className="flex h-2 w-2 relative flex-shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
              </div>
              <div className="text-[10px] sm:text-xs text-gray-500 font-bold uppercase truncate">
                Réponse rapide
              </div>
            </div>
          </button>

          {/* Facebook */}
          <button
            onClick={() => window.open('https://www.facebook.com/people/Technic-import-outre-mer/61583760578874/')}
            className="w-full flex items-center gap-3 sm:gap-5 p-3.5 sm:p-5 bg-gray-50 border border-transparent hover:border-blue-500/30 hover:bg-white rounded-2xl sm:rounded-3xl transition-all group hover:-translate-y-1 hover:shadow-xl"
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-50 text-blue-600 rounded-xl sm:rounded-2xl flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white group-hover:rotate-12 transition-all flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </div>
            <div className="text-left min-w-0 flex-1">
              <div className="font-black text-gray-900 uppercase italic text-xs sm:text-sm group-hover:text-blue-600 transition-colors truncate">
                Facebook
              </div>
              <div className="text-[10px] sm:text-xs text-gray-500 font-bold uppercase truncate">
                Actualités & Stock
              </div>
            </div>
          </button>

          {/* Appel Direct - même logique */}
          <button
            onClick={() => window.open('https://mail.google.com/mail/?view=cm&to=technicimport33@gmail.com', '_blank')}
            className="w-full flex items-center gap-3 sm:gap-5 p-3.5 sm:p-5 bg-gray-50 border border-transparent hover:border-red-600/30 hover:bg-white rounded-2xl sm:rounded-3xl transition-all group hover:-translate-y-1 hover:shadow-xl"
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-50 text-red-600 rounded-xl sm:rounded-2xl flex items-center justify-center group-hover:bg-red-600 group-hover:text-white group-hover:rotate-12 transition-all flex-shrink-0">
              <Mail size={20} className="sm:w-6 sm:h-6" strokeWidth={2.5} />
            </div>
            <div className="text-left min-w-0 flex-1">
              <div className="font-black text-gray-900 uppercase italic text-xs sm:text-sm group-hover:text-red-600 transition-colors truncate">
                Email Direct
              </div>
              <div className="text-[10px] sm:text-xs text-gray-500 font-bold uppercase truncate">
                technicimport33@gmail.com
              </div>
            </div>
          </button>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-8 py-3 sm:py-5 bg-gray-900 text-center">
          <p className="text-[10px] sm:text-xs text-white/50 font-black uppercase tracking-wider flex items-center justify-center gap-2">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse flex-shrink-0" />
            <span className="truncate">Équipe disponible · Lun - Sam</span>
          </p>
        </div>
      </div>
    </div>
  );


  const isAdmin = location.pathname === '/admin';

  return (
    <div className="min-h-screen bg-gray-100">
     {!isAdmin && (
  <>
    <Header
      language={language}
      setLanguage={setLanguage}
      cart={cart}
      setShowCart={setShowCart}
      isAuthenticated={isAuthenticated}
      handleLogout={handleLogout}
      setShowAuthModal={setShowAuthModal}
      setShowContactModal={setShowContactModal}
      t={t}
    />
    {location.pathname === '/' && (
      <>
        <Carousel />
        <div className="bg-white shadow-md py-4">
          <div className="max-w-7xl mx-auto px-4">
            <SearchBar onSearch={handleSearch} placeholder={t.search} />
          </div>
        </div>
      </>
    )}
  </>
)}

      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/automobiles" element={<AutomobilesPage />} />
        <Route path="/bateaux" element={<BateauxPage />} />
        <Route path="/bungalows" element={<BungalowsPage />} />
        <Route path="/stock" element={<StockPage />} />

        {/* UNE SEULE ROUTE FINANCING AVEC TOUTES LES PROPS */}
        <Route
          path="/financing"
          element={
            <FinancingPage
              navigate={navigate}
              setShowContactModal={setShowContactModal}
            />
          }
        />

        <Route
          path="/detail"
          element={
            <DetailPage
              selectedVehicle={selectedVehicle}
              categoryInfo={categoryInfo}
              formatPrice={formatPrice}
              // encodeImagePath={encodeImagePath}
              addToCart={addToCart}
            />
          }
        />
      </Routes>
      {!isAdmin && (
        <Footer
          navigate={navigate}
          t={t}
          categoryInfo={categoryInfo}
        />
      )}
      {showCart && <CartModal />}
      {showContactModal && <ContactModal />}
      {showAuthModal && (
        <AuthModal
          t={t}
          onClose={() => setShowAuthModal(false)}
          onLogin={async (email, password) => {
            const res = await fetch(`${API_BASE}/api/auth/login`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ email, password }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            setToken(data.token);
            setUser(data.user);
            setIsAuthenticated(true);
            setShowAuthModal(false);
          }}
          onRegister={async (email, password, name) => {
            const res = await fetch(`${API_BASE}/api/auth/register`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ email, password, name }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            setToken(data.token);
            setUser(data.user);
            setIsAuthenticated(true);
            setShowAuthModal(false);
          }}
        />
      )}
      <ToastContainer />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <VehicleShop />
    </BrowserRouter>
  );
}
